import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { message } from 'antd';

import { catalogApi } from '../../modules/api';

import {
  heroTiles as initialHeroTiles,
  featuredCollections as initialFeaturedCollections,
  curatedPrompts as initialCuratedPrompts,
  trendingPrompts as initialTrendingPrompts,
  inspirationBoards as initialInspirationBoards,
} from './catalogData';

const CatalogDataContext = createContext(null);

function mapTagNames(tags = []) {
  if (!Array.isArray(tags)) {
    return [];
  }

  return tags
    .map((tag) => {
      if (!tag) {
        return '';
      }
      if (typeof tag === 'string') {
        return tag.trim();
      }
      if (typeof tag === 'object' && tag.name) {
        return String(tag.name).trim();
      }
      return '';
    })
    .filter(Boolean);
}

function normalizeTemplateText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isCharacterSwapTemplate(template) {
  if (!template) {
    return false;
  }

  const workflowFields = [
    template.taskType,
    template.type,
    template.mode,
    template.generationMode,
    template.workflow,
  ].map(normalizeTemplateText);
  if (workflowFields.some((value) => value === 'character swap')) {
    return true;
  }

  const title = normalizeTemplateText(template.title);
  const prompt = normalizeTemplateText(template.prompt);
  return title.includes('character swap') || prompt === 'character swap';
}

const ROAD_CANYON_IMAGE_ID = 'photo-1500530855697-b586d89ba3ee';
const ROAD_CANYON_TEMPLATE_METADATA = {
  title: 'Desert road through red rock mountains',
  prompt:
    'Open highway road running through red rock canyon mountains, desert landscape, rocky valley, scenic travel photography',
  tags: ['Road', 'Mountain', 'Rock', 'Desert', 'Landscape'],
};

function applyKnownTemplateVisualMetadata(template) {
  if (!template) {
    return template;
  }

  const imageUrl = String(template.imageUrl || template.url || '');
  if (!imageUrl.includes(ROAD_CANYON_IMAGE_ID)) {
    return template;
  }

  return {
    ...template,
    title: ROAD_CANYON_TEMPLATE_METADATA.title,
    prompt: ROAD_CANYON_TEMPLATE_METADATA.prompt,
    tags: Array.from(new Set([
      ...mapTagNames(template.tags),
      ...ROAD_CANYON_TEMPLATE_METADATA.tags,
    ])),
  };
}

export function mapCollection(collection) {
  if (!collection) {
    return null;
  }

  const artworks = Array.isArray(collection.artworks)
    ? collection.artworks
        .map((artwork, index) => {
          if (!artwork || !artwork.imageUrl || isCharacterSwapTemplate(artwork)) {
            return null;
          }

          return applyKnownTemplateVisualMetadata({
            id: artwork.id || `${collection.id}-artwork-${index + 1}`,
            url: artwork.imageUrl,
            title: artwork.title || `${collection.title} artwork ${index + 1}`,
            prompt: artwork.prompt || artwork.title || '',
            tags: mapTagNames(artwork.tags),
          });
        })
        .filter(Boolean)
    : [];

  return {
    id: collection.id,
    title: collection.title,
    description: collection.description || '',
    cover: collection.coverImageUrl || (artworks[0] && artworks[0].url) || null,
    previews: artworks.slice(1, 3).map((artwork) => artwork.url),
    tags: mapTagNames(collection.tags),
    curator: collection.curator || 'Curated by SoAlive',
    stats: {
      artworks:
        (collection.stats && collection.stats.artworkCount)
        || artworks.length
        || 0,
      updated: 'Recently updated',
      updatedISO: collection.updatedAt || null,
    },
    shouldGenerate: true,
    artworks,
    visibility: collection.visibility || 'public',
    isFeatured: Boolean(collection.isFeatured),
    featuredOrder: Number.isFinite(collection.featuredOrder) ? collection.featuredOrder : 0,
    updatedAt: collection.updatedAt || null,
    createdAt: collection.createdAt || null,
  };
}

export function mapTemplate(template, index = 0) {
  if (!template || !template.imageUrl || isCharacterSwapTemplate(template)) {
    return null;
  }

  return applyKnownTemplateVisualMetadata({
    id: template.templateId || template.id || `template-${index + 1}`,
    templateId: template.templateId || template.id || `template-${index + 1}`,
    source: template.source || 'catalog',
    sourceCollectionId: template.sourceCollectionId || null,
    sourceArtworkId: template.sourceArtworkId || null,
    sourceCollectionTitle: template.sourceCollectionTitle || '',
    title: template.title || `Template ${index + 1}`,
    imageUrl: template.imageUrl,
    url: template.imageUrl,
    prompt: template.prompt || template.title || '',
    tags: mapTagNames(template.tags),
    visibility: template.visibility || 'public',
    isFeatured: Boolean(template.isFeatured),
    featuredOrder: Number.isFinite(template.featuredOrder) ? template.featuredOrder : 0,
    matchReason: template.matchReason || null,
    updatedAt: template.updatedAt || null,
    createdAt: template.createdAt || null,
  });
}

export function templatesFromCollections(collections = []) {
  if (!Array.isArray(collections)) {
    return [];
  }

  return collections.flatMap((collection) => {
    const mappedCollection =
      collection && collection.cover !== undefined ? collection : mapCollection(collection);
    if (!mappedCollection || !Array.isArray(mappedCollection.artworks)) {
      return [];
    }

    return mappedCollection.artworks
      .filter((artwork) => !isCharacterSwapTemplate(artwork))
      .map((artwork, index) => applyKnownTemplateVisualMetadata({
        id: `artwork:${mappedCollection.id}:${artwork.id}`,
        templateId: `artwork:${mappedCollection.id}:${artwork.id}`,
        source: 'catalog',
        sourceCollectionId: mappedCollection.id,
        sourceArtworkId: artwork.id,
        sourceCollectionTitle: mappedCollection.title,
        title: artwork.title || `${mappedCollection.title} template ${index + 1}`,
        imageUrl: artwork.url,
        url: artwork.url,
        prompt: artwork.prompt || mappedCollection.description || artwork.title || '',
        tags: Array.from(new Set([...(mappedCollection.tags || []), ...(artwork.tags || [])])),
        visibility: mappedCollection.visibility || 'public',
        isFeatured: Boolean(mappedCollection.isFeatured),
        featuredOrder: Number.isFinite(mappedCollection.featuredOrder)
          ? mappedCollection.featuredOrder
          : 0,
        matchReason: null,
        updatedAt: mappedCollection.updatedAt || null,
        createdAt: mappedCollection.createdAt || null,
      }));
  });
}

function mapBoard(board) {
  if (!board) {
    return null;
  }

  return {
    ...board,
    thumbnails: Array.isArray(board.thumbnailUrls) ? board.thumbnailUrls : [],
  };
}

export const CatalogDataProvider = ({ children }) => {
  const [collections, setCollections] = useState(initialFeaturedCollections);
  const [templates, setTemplates] = useState(() => templatesFromCollections(initialFeaturedCollections));
  const [boards, setBoards] = useState(initialInspirationBoards);
  const [curatedPrompts, setCuratedPrompts] = useState(initialCuratedPrompts);
  const [trendingPrompts, setTrendingPrompts] = useState(initialTrendingPrompts);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      try {
        const response = await catalogApi.fetchCatalog();
        if (cancelled) {
          return;
        }

        const data = response && response.data ? response.data : {};

        if (Array.isArray(data.collections) && data.collections.length > 0) {
          const mappedCollections = data.collections.map(mapCollection).filter(Boolean);
          setCollections(mappedCollections);

          if (Array.isArray(data.templates) && data.templates.length > 0) {
            setTemplates(data.templates.map(mapTemplate).filter(Boolean));
          } else {
            setTemplates(templatesFromCollections(mappedCollections));
          }
        }

        if (Array.isArray(data.inspirationBoards)) {
          setBoards(data.inspirationBoards.map(mapBoard).filter(Boolean));
        }

        if (Array.isArray(data.curatedPrompts) && data.curatedPrompts.length > 0) {
          setCuratedPrompts(data.curatedPrompts);
        }

        if (Array.isArray(data.trendingPrompts) && data.trendingPrompts.length > 0) {
          setTrendingPrompts(data.trendingPrompts);
        }
      } catch (error) {
        if (!cancelled && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Failed to load catalog overview from API', error);
        }
        if (!cancelled) {
          message.warning('Templates are using fallback content while live data loads.');
        }
      }
    };

    loadCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  const searchPublicCollections = useCallback(async (query) => {
    const response = await catalogApi.searchCatalog({ query });
    const data = response && response.data ? response.data : {};
    return {
      enabled: Boolean(data.enabled),
      items: Array.isArray(data.items)
        ? data.items.map(mapTemplate).filter(Boolean)
        : [],
      collections: Array.isArray(data.collections)
        ? data.collections.map(mapCollection).filter(Boolean)
        : [],
    };
  }, []);

  const value = useMemo(
    () => ({
      heroTiles: initialHeroTiles,
      curatedPrompts,
      trendingPrompts,
      collections,
      setCollections,
      templates,
      setTemplates,
      searchPublicCollections,
      boards,
      setBoards,
    }),
    [boards, collections, curatedPrompts, searchPublicCollections, templates, trendingPrompts],
  );

  return <CatalogDataContext.Provider value={value}>{children}</CatalogDataContext.Provider>;
};

export const useCatalogData = () => {
  const context = useContext(CatalogDataContext);

  if (!context) {
    throw new Error('useCatalogData must be used within a CatalogDataProvider');
  }

  return context;
};
